/**
 * SystemEventChip — collapsed rendering for platform-injected user turns
 * (`source: 'event_notification'`, e.g. the credential-continuation
 * "SYSTEM NOTE" nudge). These are real persisted turns the agent needs for
 * context, but they're not something the user typed — a full user bubble of
 * internal instructions reads as noise. Instead: a centered pill with the
 * note's first line, expandable to the full text (the AskUserSummary /
 * StructuredAnswerBlock treatment, one size smaller).
 */

import { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, Zap } from 'lucide-react';

const LABEL_MAX = 96;

export function SystemEventChip({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const body = text.replace(/^SYSTEM NOTE:\s*/i, '').trim();
  const firstLine = (body.split('\n')[0] ?? '').trim();
  const label =
    firstLine.length > LABEL_MAX ? `${firstLine.slice(0, LABEL_MAX - 1)}…` : firstLine;

  return (
    // Right-aligned like the user bubble it replaces (injected turns are
    // user-role), so the thread's who-said-what geometry stays consistent.
    <div className="col-start-1 col-end-[-1] my-1 flex flex-col items-end gap-1.5">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-muted/60 px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <Zap aria-hidden className="h-3 w-3 shrink-0" />
        <span className="truncate">{label || 'System event'}</span>
        {expanded ? (
          <ChevronUpIcon className="h-3 w-3 shrink-0" />
        ) : (
          <ChevronDownIcon className="h-3 w-3 shrink-0" />
        )}
      </button>
      {expanded && (
        <div className="max-w-[85%] whitespace-pre-wrap rounded-lg border border-border bg-muted/40 px-3 py-2 text-left text-xs text-muted-foreground">
          {body}
        </div>
      )}
    </div>
  );
}
